// Admin overview — KPI dashboard + governance surfaces.
// Top of the page: KPI tiles refreshed every 30s.
// Bottom: links to Maintenance / Trust bundles / Retention / Audit.

import { Link } from 'react-router-dom';
import {
  Wrench,
  ShieldCheck,
  Trash2,
  Activity,
  ChevronRight,
  Box,
  Package,
  FolderTree,
  Bot,
  LogIn,
  Download,
  AlertOctagon,
  Users,
  Rocket,
  Sparkles,
  Code2,
  type LucideIcon,
} from 'lucide-react';
import { AdminShell } from '../../components/AdminShell';
import { Card, CardBody, CardHeader } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { CopyIconButton } from '../../components/ui/CopyIconButton';
import { VersionStatusCard } from '../../components/VersionStatusCard';
import { useMaintenance } from '../../api/maintenance';
import { useKPIs, type KPISnapshot } from '../../api/kpis';
import { useBackupStatus, type BackupStatus } from '../../api/backup';
import {
  useBackupVerification,
  useBackupVerificationHistory,
  type BackupVerificationSummary,
} from '../../api/backupVerification';
import { useLicense, type LicenseStatus } from '../../api/license';
import { usePublicConfig } from '../../api/publicConfig';
import { useAdminVersion } from '../../api/version';
import { useSlugAudit } from '../../api/slugAudit';
import { useState } from 'react';

interface Tile {
  to: string;
  Icon: LucideIcon;
  title: string;
  description: string;
  status?: { tone: 'success' | 'warning' | 'neutral' | 'accent'; label: string };
}

interface KPICard {
  Icon: LucideIcon;
  label: string;
  value: string;
  hint?: string;
}

function fmtN(n: number | undefined): string {
  if (n === undefined || n === null) return '—';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return String(n);
}

function buildKPICards(k: KPISnapshot): KPICard[] {
  const dl = k.downloads_by_kind || {};
  const lm = k.logins_by_method || {};
  return [
    { Icon: FolderTree, label: 'Projects', value: fmtN(k.projects_total) },
    { Icon: Box, label: 'Repositories', value: fmtN(k.repositories_total) },
    { Icon: Package, label: 'Artifacts', value: fmtN(k.artifacts_total) },
    {
      Icon: Download,
      label: 'Downloads (30d)',
      value: fmtN(k.downloads_30d),
      hint: `total ${fmtN(k.downloads_total)}`,
    },
    {
      Icon: Download,
      label: 'Downloads · local',
      value: fmtN(dl['local'] ?? 0),
    },
    {
      Icon: Download,
      label: 'Downloads · remote',
      value: fmtN(dl['remote'] ?? 0),
    },
    {
      Icon: Download,
      label: 'Downloads · virtual',
      value: fmtN(dl['virtual'] ?? 0),
    },
    {
      Icon: Bot,
      label: 'Service accounts',
      value: fmtN(k.service_accounts_active),
      hint: `${fmtN(k.service_accounts_total)} total`,
    },
    {
      Icon: LogIn,
      label: 'Logins (30d)',
      value: fmtN(k.logins_30d),
      hint: [
        lm.local ? `${lm.local} local` : null,
        lm.saml ? `${lm.saml} SAML` : null,
        lm.oidc ? `${lm.oidc} OIDC` : null,
      ]
        .filter(Boolean)
        .join(' · ') || '',
    },
    {
      Icon: Users,
      label: 'Users',
      value: fmtN(k.users_active),
      hint: `${fmtN(k.users_total)} total`,
    },
    { Icon: AlertOctagon, label: 'Advisories', value: fmtN(k.advisories_total) },
    {
      Icon: ShieldCheck,
      label: 'Blocks',
      value: fmtN(k.blocks_active),
    },
  ];
}

function fmtDuration(seconds: number | undefined): string {
  if (seconds === undefined || seconds === null) return '—';
  if (seconds < 60) return `${seconds}s ago`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86_400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86_400)}d ago`;
}

function licenseCardClassName(lic: LicenseStatus): string {
  const base = 'block rounded-md border p-4 mb-4 hover:bg-bg-hover/40 transition-colors ';
  if (lic.state === 'trial_active' && lic.days_remaining > 14) return base + 'border-accent/40 bg-accent/5';
  if (lic.days_remaining > 7) return base + 'border-warning/40 bg-warning/5';
  return base + 'border-danger/40 bg-danger/5';
}

function backupStatusBadge(backup: BackupStatus) {
  if (!backup.enabled) return <Badge tone="neutral">Off</Badge>;
  if (backup.pending_failures > 0) {
    return <Badge tone="danger">{backup.pending_failures} unrecovered failures</Badge>;
  }
  return <Badge tone="success">Healthy</Badge>;
}

function verificationStatusBadge(verify: BackupVerificationSummary) {
  if (verify.last_pass_at == null) return <Badge tone="neutral">No runs yet</Badge>;
  if (verify.seconds_since_last_pass !== undefined && verify.seconds_since_last_pass > 691_200) {
    return <Badge tone="danger">Stale ({fmtDuration(verify.seconds_since_last_pass)})</Badge>;
  }
  return <Badge tone="success">Healthy</Badge>;
}

function verificationRowTone(status: 'running' | 'pass' | 'fail'): 'success' | 'danger' | 'neutral' {
  if (status === 'pass') return 'success';
  if (status === 'fail') return 'danger';
  return 'neutral';
}

function platformMetricsDescription(kpi: KPISnapshot | undefined, loadingKPI: boolean): string {
  if (kpi) return `Snapshot at ${new Date(kpi.snapshot_at).toLocaleString()}`;
  if (loadingKPI) return 'Loading…';
  return 'No data yet.';
}

function SlugAuditAlert({ slugAudit }: Readonly<{ slugAudit: { projects: unknown[]; repositories: unknown[] } }>) {
  return (
    <Link
      to="/admin/slug-audit"
      className="block rounded-md border border-warning/40 bg-warning/5 p-4 mb-4 hover:bg-warning/10 transition-colors"
    >
      <div className="flex items-center justify-between gap-4">
        <div>
          <p className="orb-eyebrow text-fg-muted">Naming convention</p>
          <p className="text-fg font-medium mt-1">
            {slugAudit.projects.length} legacy-named project
            {slugAudit.projects.length === 1 ? '' : 's'} · {slugAudit.repositories.length} repositor
            {slugAudit.repositories.length === 1 ? 'y' : 'ies'}
          </p>
          <p className="text-fg-muted text-sm mt-1">
            Slugs that pre-date the current convention (lowercase + dots).
            Click to review the suggested-rename plan.
          </p>
        </div>
        <ChevronRight className="h-5 w-5 text-fg-muted" />
      </div>
    </Link>
  );
}

function LicenseAlert({ lic }: Readonly<{ lic: LicenseStatus }>) {
  return (
    <Link to="/admin/license" className={licenseCardClassName(lic)}>
      <div className="flex items-center justify-between gap-4">
        <div>
          <p className="orb-eyebrow text-fg-muted">Licensing</p>
          <p className="text-fg font-medium mt-1">
            {lic.state === 'trial_active' && `Trial — ${lic.days_remaining} days remaining`}
            {lic.state === 'trial_expired' && 'Trial expired — apply a commercial license to re-enable writes'}
            {lic.state === 'licensed_expired' && 'License expired — renew to keep write APIs available'}
            {lic.state === 'licensed_active' && lic.days_remaining <= 30 && `License expires in ${lic.days_remaining} days`}
          </p>
          <p className="text-fg-muted text-sm mt-1">
            Click to enter / renew your license envelope.
          </p>
        </div>
        <ChevronRight className="h-5 w-5 text-fg-muted" />
      </div>
    </Link>
  );
}

function BackupMirrorCard({ backup }: Readonly<{ backup: BackupStatus }>) {
  return (
    <Card>
      <CardHeader
        title="S3 backup mirror"
        description={
          backup.enabled
            ? `Dual-write to ${backup.endpoint} / ${backup.bucket}`
            : 'Not configured — set ORBITALREG_S3_BACKUP_* env vars to enable'
        }
        actions={backupStatusBadge(backup)}
      />
      {backup.enabled && (
        <CardBody>
          <div className="flex flex-wrap gap-2 mb-4">
            {backup.async_mode && <Badge tone="accent">async</Badge>}
            {backup.read_fallback && <Badge tone="nebula">read-fallback</Badge>}
            {backup.strict_mode && <Badge tone="warning">strict</Badge>}
            {backup.pending_async > 0 && (
              <Badge tone="warning">{backup.pending_async} pending async</Badge>
            )}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <div>
              <p className="orb-section-title text-fg-muted">Writes attempted</p>
              <p className="text-fg text-lg font-display">{(backup.writes_attempted ?? 0).toLocaleString()}</p>
            </div>
            <div>
              <p className="orb-section-title text-fg-muted">Writes OK</p>
              <p className="text-success text-lg font-display">{(backup.writes_ok ?? 0).toLocaleString()}</p>
            </div>
            <div>
              <p className="orb-section-title text-fg-muted">Writes failed</p>
              <p className={`text-lg font-display ${backup.writes_failed ? 'text-danger' : 'text-fg-muted'}`}>
                {(backup.writes_failed ?? 0).toLocaleString()}
              </p>
            </div>
            <div>
              <p className="orb-section-title text-fg-muted">Recovered</p>
              <p className="text-fg text-lg font-display">{backup.recovered_total.toLocaleString()}</p>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm mt-3">
            <div>
              <p className="orb-section-title text-fg-muted">Last success</p>
              <p className="text-fg-muted text-xs">
                {backup.last_success_at ? new Date(backup.last_success_at).toLocaleString() : '—'}
              </p>
            </div>
            <div>
              <p className="orb-section-title text-fg-muted">Last failure</p>
              <p className="text-fg-muted text-xs">
                {backup.last_failure_at ? new Date(backup.last_failure_at).toLocaleString() : '—'}
              </p>
            </div>
          </div>
          {backup.last_failure_msg && (
            <p className="text-danger font-mono text-xs mt-3">
              Last failure: {backup.last_failure_msg}
            </p>
          )}
        </CardBody>
      )}
    </Card>
  );
}

function BackupVerificationHistoryTable({ history }: Readonly<{ history: { id: string; started_at: string; status: 'running' | 'pass' | 'fail'; backup_source: string; backup_id: string; duration_ms: number | null }[] }>) {
  return (
    <div className="mt-3 border border-border rounded-md overflow-hidden">
      <table className="w-full text-xs">
        <thead className="bg-bg-inset/60 text-fg-muted">
          <tr>
            <th className="text-left px-2 py-1.5 font-medium">Started</th>
            <th className="text-left px-2 py-1.5 font-medium">Status</th>
            <th className="text-left px-2 py-1.5 font-medium">Source</th>
            <th className="text-left px-2 py-1.5 font-medium">Backup</th>
            <th className="text-right px-2 py-1.5 font-medium">Duration</th>
          </tr>
        </thead>
        <tbody>
          {history.length === 0 && (
            <tr>
              <td colSpan={5} className="text-center px-2 py-3 text-fg-muted">
                No runs yet — the CronJob has not produced an outcome.
              </td>
            </tr>
          )}
          {history.map((row) => (
            <tr key={row.id} className="border-t border-border">
              <td className="px-2 py-1.5 text-fg-muted">
                {new Date(row.started_at).toLocaleString()}
              </td>
              <td className="px-2 py-1.5">
                <Badge tone={verificationRowTone(row.status)}>
                  {row.status}
                </Badge>
              </td>
              <td className="px-2 py-1.5 text-fg-muted">{row.backup_source}</td>
              <td className="px-2 py-1.5 font-mono text-fg-muted">
                <span className="inline-flex items-center gap-1 max-w-[18ch]">
                  <span className="truncate" title={row.backup_id}>{row.backup_id}</span>
                  <CopyIconButton value={row.backup_id} label="Copy backup ID" />
                </span>
              </td>
              <td className="px-2 py-1.5 text-right text-fg-muted">
                {row.duration_ms == null ? '—' : `${(row.duration_ms / 1000).toFixed(1)}s`}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function BackupVerificationCard({
  verify,
  historyOpen,
  onToggleHistory,
  history,
}: Readonly<{
  verify: BackupVerificationSummary;
  historyOpen: boolean;
  onToggleHistory: () => void;
  history: { id: string; started_at: string; status: 'running' | 'pass' | 'fail'; backup_source: string; backup_id: string; duration_ms: number | null }[] | undefined;
}>) {
  return (
    <Card>
      <CardHeader
        title="DB backup verification"
        description="Weekly restore test — proves backups are recoverable, not just created. Driven by the chart's backup-verify CronJob."
        actions={verificationStatusBadge(verify)}
      />
      <CardBody>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          <div>
            <p className="orb-section-title text-fg-muted">Total runs</p>
            <p className="text-fg text-lg font-display">{verify.runs_total.toLocaleString()}</p>
          </div>
          <div>
            <p className="orb-section-title text-fg-muted">Pass</p>
            <p className="text-success text-lg font-display">{verify.runs_pass.toLocaleString()}</p>
          </div>
          <div>
            <p className="orb-section-title text-fg-muted">Fail</p>
            <p className={`text-lg font-display ${verify.runs_fail ? 'text-danger' : 'text-fg-muted'}`}>
              {verify.runs_fail.toLocaleString()}
            </p>
          </div>
          <div>
            <p className="orb-section-title text-fg-muted">Last successful</p>
            <p className="text-fg-muted text-xs">
              {verify.last_pass_at ? new Date(verify.last_pass_at).toLocaleString() : '—'}
            </p>
          </div>
        </div>
        {verify.last_fail_at && (
          <p className="text-warning font-mono text-xs mt-3">
            Last failure {new Date(verify.last_fail_at).toLocaleString()}: {verify.last_fail_error || 'no detail'}
          </p>
        )}
        <button
          type="button"
          onClick={onToggleHistory}
          className="mt-3 text-xs text-nebula hover:text-nebula/80 underline-offset-2 hover:underline"
        >
          {historyOpen ? 'Hide history' : 'Show recent verification history'}
        </button>
        {historyOpen && history && <BackupVerificationHistoryTable history={history} />}
      </CardBody>
    </Card>
  );
}

export function AdminOverview() {
  const { data: maint } = useMaintenance();
  const { data: kpi, isLoading: loadingKPI } = useKPIs();
  const { data: backup } = useBackupStatus();
  const { data: verify } = useBackupVerification();
  const { data: lic } = useLicense();
  const { data: pubConfig } = usePublicConfig();
  const { data: ver } = useAdminVersion();
  const { data: slugAudit } = useSlugAudit();
  const [historyOpen, setHistoryOpen] = useState(false);
  const { data: history } = useBackupVerificationHistory(30);

  const tiles: Tile[] = [
    {
      to: '/admin/maintenance',
      Icon: Wrench,
      title: 'Maintenance mode',
      description: 'Put the platform into read-only mode for migrations or upstream cutovers.',
      status: maint?.active
        ? { tone: 'warning', label: 'Active — read-only' }
        : { tone: 'success', label: 'Available' },
    },
    {
      to: '/admin/trust',
      Icon: ShieldCheck,
      title: 'Trust bundles',
      description: 'Per-repo CMS / OpenPGP / RSA trust sets that gate verify-on-pull and push.',
      status: { tone: 'neutral', label: 'View' },
    },
    {
      to: '/admin/retention',
      Icon: Trash2,
      title: 'Retention policies',
      description: 'Per-repo rules that prune old artifacts based on age, version count, or properties.',
      status: { tone: 'neutral', label: 'View' },
    },
    {
      to: '/admin/audit',
      Icon: Activity,
      title: 'Audit log',
      description: 'Verify gate dispatches, retention deletions, admin actions, build-info submissions.',
      status: { tone: 'neutral', label: 'View' },
    },
    {
      to: '/admin/system/setup',
      Icon: Rocket,
      title: 'Setup wizard',
      description: 'Re-run the first-run wizard to create projects, mint tokens, and configure trust.',
      status: { tone: 'neutral', label: 'Open' },
    },
    {
      to: '/admin/seed',
      Icon: Sparkles,
      title: 'Demo data',
      description: 'Populate the deployment with realistic demo projects, repos, and findings for evaluations.',
      status: { tone: 'neutral', label: 'Open' },
    },
    {
      to: '/admin/api-reference',
      Icon: Code2,
      title: 'API playground',
      description: 'Interactive REST reference with try-it-now requests against this deployment.',
      status: { tone: 'neutral', label: 'Open' },
    },
  ];

  const cards = kpi ? buildKPICards(kpi) : [];

  return (
    <AdminShell
      title={
        <span className="flex items-center gap-3 flex-wrap">
          Admin overview
          {pubConfig?.version && (
            <span
              className="inline-flex items-center px-2 py-0.5 rounded font-mono text-xs bg-bg-inset border border-border text-fg-muted"
              title={[
                pubConfig.build_time
                  ? `Built ${new Date(pubConfig.build_time).toLocaleString()}`
                  : 'Build time unknown',
                ver?.git_commit ? `Commit ${ver.git_commit}` : null,
                ver?.go_version ? `Runtime ${ver.go_version}` : null,
              ]
                .filter(Boolean)
                .join('\n')}
            >
              {pubConfig.version}
            </span>
          )}
        </span>
      }
      description="Platform-wide totals plus governance surfaces. KPIs auto-refresh once per 24 hours; reload the page for fresh numbers."
    >
      <div className="mb-4">
        <VersionStatusCard />
      </div>

      {slugAudit && (slugAudit.projects.length > 0 || slugAudit.repositories.length > 0) && (
        <SlugAuditAlert slugAudit={slugAudit} />
      )}

      {lic && (lic.state === 'trial_active' || lic.state === 'trial_expired' || lic.state === 'licensed_expired' || (lic.state === 'licensed_active' && lic.days_remaining <= 30)) && (
        <LicenseAlert lic={lic} />
      )}

      {backup && <BackupMirrorCard backup={backup} />}

      {verify && (verify.runs_total > 0 || verify.last_pass_at != null) && (
        <BackupVerificationCard
          verify={verify}
          historyOpen={historyOpen}
          onToggleHistory={() => setHistoryOpen((v) => !v)}
          history={history}
        />
      )}

      <Card>
        <CardHeader
          title="Platform metrics"
          description={platformMetricsDescription(kpi, loadingKPI)}
        />
        <CardBody>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3">
            {cards.map((c) => (
              <div
                key={c.label}
                className="rounded-md border border-border bg-bg-inset/40 p-3 min-w-0"
              >
                <div className="flex items-center gap-1.5 text-fg-muted">
                  <c.Icon className="h-3 w-3 shrink-0" />
                  <p className="text-[10px] uppercase tracking-[0.12em] font-medium leading-tight">
                    {c.label}
                  </p>
                </div>
                <p className="text-xl font-display text-fg leading-tight mt-1.5">
                  {c.value}
                </p>
                {c.hint && (
                  <p className="text-[10px] text-fg-subtle mt-0.5 leading-tight" title={c.hint}>
                    {c.hint}
                  </p>
                )}
              </div>
            ))}
          </div>
        </CardBody>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {tiles.map(({ to, Icon, title, description, status }) => (
          <Link
            key={to}
            to={to}
            className="orb-card hover:border-nebula/50 hover:shadow-glow-accent transition-all group"
          >
            <CardBody className="flex items-start gap-4">
              <div className="h-10 w-10 rounded-md bg-nebula/10 border border-nebula/30 flex items-center justify-center shrink-0">
                <Icon className="h-5 w-5 text-nebula" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <h3 className="font-semibold text-fg">{title}</h3>
                  {status && <Badge tone={status.tone}>{status.label}</Badge>}
                </div>
                <p className="text-sm text-fg-muted mt-1 leading-relaxed">{description}</p>
              </div>
              <ChevronRight className="h-4 w-4 text-fg-subtle group-hover:text-nebula self-center shrink-0 transition-colors" />
            </CardBody>
          </Link>
        ))}
      </div>
    </AdminShell>
  );
}
